"use client";

import { CategoryType } from "@/types/category.type";
import {
  Card,
  IconButton,
  Skeleton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import ProductCard from "@/components/product-card";
import axios from "axios";
import config from "@/app.config";
import { ProductType } from "@/types/product.type";
import SparkleEffect from "@/components/sparkle-effect";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import routes from "@/app.route";

export default function CategoryWiseProducts({ handle }: { handle: string }) {
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<CategoryType | null>(null);
  const [products, setProducts] = useState<ProductType[]>([]);
  const matches = useMediaQuery("(min-width:1024px)");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${config.app.apiBaseUrl}/api/v2/collections/${handle}?limit=7`,
        );

        setCategory(response.data.data);

        setProducts(response.data.products);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-between gap-3 px-4 py-4">
          <div className="text-xl font-semibold">
            <Skeleton width={180} variant="rounded" />
          </div>
        </div>
        <div className="px-0 md:px-4">
          <Swiper
            modules={[Navigation]}
            spaceBetween={12}
            slidesPerView="auto"
            navigation
          >
            {[1, 2, 3, 4, 5, 6].map((_, i) => (
              <SwiperSlide
                className="md:max-w-[300px] max-w-[165px] pb-8"
                key={i}
              >
                <SparkleEffect>
                  <div className="group hover:shadow-xl duration-500 border border-zinc-200 bg-white block rounded-lg overflow-hidden">
                    <figure>
                      <div className="md:size-[300px] size-[165px]"></div>
                    </figure>
                    <div className="bg-white px-3 py-6 space-y-2">
                      <Skeleton width="100%" variant="rounded" />
                      <Skeleton width={220} variant="rounded" />
                      <Skeleton width={80} variant="rounded" />
                    </div>
                  </div>
                </SparkleEffect>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Card>
    );
  }

  if (category == null || products.length == 0) return null;

  return (
    <>
      <Card>
        <div className="flex items-center justify-between gap-3 px-4 py-4">
          <div className="text-xl font-semibold">{category.title}</div>
          <Tooltip title={`View ${category.title}`}>
            <IconButton
              aria-label={`View ${category.title} category`}
              href={routes.category.url.replace(":slug", category.handle)}
              size="small"
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
              }}
            >
              <ChevronRightRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className="px-4">
          <Swiper
            modules={[Autoplay, Navigation]}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            spaceBetween={12}
            slidesPerView="auto"
            loop
            navigation={matches}
          >
            {products.map((product, i) => (
              <SwiperSlide
                className="md:max-w-[300px] max-w-[165px] pb-8"
                key={i}
              >
                <ProductCard product={product} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Card>
    </>
  );
}
